import type { BoundsType, PositionType, ReactZoomPanPinchContext, ReactZoomPanPinchState } from "react-zoom-pan-pinch";

const getOffset = (element: HTMLElement, wrapper: HTMLElement, content: HTMLElement, state: ReactZoomPanPinchState) => {
  const offset = element.getBoundingClientRect();
  const wrapperOffset = wrapper.getBoundingClientRect();
  const contentOffset = content.getBoundingClientRect();

  const xOff = wrapperOffset.x * state.scale;
  const yOff = wrapperOffset.y * state.scale;

  return {
    x: (offset.x - contentOffset.x + xOff) / state.scale,
    y: (offset.y - contentOffset.y + yOff) / state.scale,
  };
};

const checkZoomBounds = (
  zoom: number,
  minScale: number,
  maxScale: number,
  zoomPadding: number,
  enablePadding: boolean,
): number => {
  const scalePadding = enablePadding ? zoomPadding : 0;
  const minScaleWithPadding = minScale - scalePadding;

  if (!Number.isNaN(maxScale) && zoom >= maxScale) return maxScale;
  if (!Number.isNaN(minScale) && zoom > minScaleWithPadding) return zoom - minScaleWithPadding;
  if (!Number.isNaN(minScale) && zoom <= minScaleWithPadding) return minScaleWithPadding;
  return zoom;
};

const calculateBounds = (contextInstance: ReactZoomPanPinchContext, newScale: number): BoundsType => {
  const getComponentsSizes = (wrapperComponent: HTMLDivElement, contentComponent: HTMLDivElement, newScale: number) => {
    const wrapperWidth = wrapperComponent.offsetWidth;
    const wrapperHeight = wrapperComponent.offsetHeight;

    const contentWidth = contentComponent.offsetWidth;
    const contentHeight = contentComponent.offsetHeight;

    const newContentWidth = contentWidth * newScale;
    const newContentHeight = contentHeight * newScale;
    const newDiffWidth = wrapperWidth - newContentWidth;
    const newDiffHeight = wrapperHeight - newContentHeight;

    return {
      wrapperWidth,
      wrapperHeight,
      newContentWidth,
      newDiffWidth,
      newContentHeight,
      newDiffHeight,
    };
  };

  const getBounds = (
    wrapperWidth: number,
    newContentWidth: number,
    diffWidth: number,
    wrapperHeight: number,
    newContentHeight: number,
    diffHeight: number,
    centerZoomedOut: boolean,
  ): BoundsType => {
    const scaleWidthFactor = wrapperWidth > newContentWidth ? diffWidth * (centerZoomedOut ? 1 : 0.5) : 0;
    const scaleHeightFactor = wrapperHeight > newContentHeight ? diffHeight * (centerZoomedOut ? 1 : 0.5) : 0;

    const minPositionX = wrapperWidth - newContentWidth - scaleWidthFactor;
    const maxPositionX = scaleWidthFactor;
    const minPositionY = wrapperHeight - newContentHeight - scaleHeightFactor;
    const maxPositionY = scaleHeightFactor;

    return { minPositionX, maxPositionX, minPositionY, maxPositionY };
  };

  const { wrapperComponent, contentComponent } = contextInstance;
  const { centerZoomedOut } = contextInstance.setup;

  if (!wrapperComponent || !contentComponent) {
    throw new Error("Components are not mounted");
  }

  const { wrapperWidth, wrapperHeight, newContentWidth, newDiffWidth, newContentHeight, newDiffHeight } =
    getComponentsSizes(wrapperComponent, contentComponent, newScale);

  return getBounds(
    wrapperWidth,
    newContentWidth,
    newDiffWidth,
    wrapperHeight,
    newContentHeight,
    newDiffHeight,
    centerZoomedOut,
  );
};

const getMouseBoundedPosition = (
  positionX: number,
  positionY: number,
  bounds: BoundsType,
  limitToBounds: boolean,
  paddingValueX: number,
  paddingValueY: number,
  wrapperComponent: HTMLDivElement | null,
): PositionType => {
  const roundNumber = (num: number, decimal: number) => {
    return Number(num.toFixed(decimal));
  };

  const boundLimiter = (value: number, minBound: number, maxBound: number, isActive: boolean): number => {
    if (!isActive) return roundNumber(value, 2);
    if (value < minBound) return roundNumber(minBound, 2);
    if (value > maxBound) return roundNumber(maxBound, 2);
    return roundNumber(value, 2);
  };

  const { minPositionX, minPositionY, maxPositionX, maxPositionY } = bounds;

  let paddingX = 0;
  let paddingY = 0;

  if (wrapperComponent) {
    paddingX = paddingValueX;
    paddingY = paddingValueY;
  }

  const x = boundLimiter(positionX, minPositionX - paddingX, maxPositionX + paddingX, limitToBounds);

  const y = boundLimiter(positionY, minPositionY - paddingY, maxPositionY + paddingY, limitToBounds);
  return { x, y };
};

const calculateZoomToNodes = (
  contextInstance: ReactZoomPanPinchContext,
  nodes: HTMLElement[],
  customZoom?: number,
): { positionX: number; positionY: number; scale: number } => {
  const { wrapperComponent, contentComponent, transformState } = contextInstance;
  const { limitToBounds, minScale, maxScale } = contextInstance.setup;
  if (!wrapperComponent || !contentComponent) return transformState;

  const wrapperRect = wrapperComponent.getBoundingClientRect();
  const nodesRect = nodes.map(node => node.getBoundingClientRect());
  const nodesWidth =
    Math.max(...nodesRect.map(rect => rect.x + rect.width)) - Math.min(...nodesRect.map(rect => rect.x));
  const nodesHeight =
    Math.max(...nodesRect.map(rect => rect.y + rect.height)) - Math.min(...nodesRect.map(rect => rect.y));
  const nodesOffset = nodes.map(node => getOffset(node, wrapperComponent, contentComponent, transformState));

  const nodeLeft = Math.min(...nodesOffset.map(offset => offset.x));
  const nodeTop = Math.min(...nodesOffset.map(offset => offset.y));
  const nodeWidth = nodesWidth / transformState.scale;
  const nodeHeight = nodesHeight / transformState.scale;

  const scaleX = wrapperComponent.offsetWidth / nodeWidth;
  const scaleY = wrapperComponent.offsetHeight / nodeHeight;

  const newScale = checkZoomBounds(customZoom || Math.min(scaleX, scaleY), minScale, maxScale, 0.7, true);

  const offsetX = (wrapperRect.width - nodeWidth * newScale) / 2;
  const offsetY = (wrapperRect.height - nodeHeight * newScale) / 2;

  const newPositionX = (wrapperRect.left - nodeLeft) * newScale + offsetX;
  const newPositionY = (wrapperRect.top - nodeTop) * newScale + offsetY;

  const bounds = calculateBounds(contextInstance, newScale);

  const { x, y } = getMouseBoundedPosition(newPositionX, newPositionY, bounds, limitToBounds, 0, 0, wrapperComponent);

  return { positionX: x, positionY: y, scale: newScale };
};

export { calculateZoomToNodes };
